/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/string/ParseFileSize.ts
 * @create:      2025-06-26 13:32:54.300
 * @modify:      2025-07-11 21:19:12.846
 * @version:     6.0.0
 * @times:       6
 * @lines:       93
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { GetLeadingNumber } from "./GetLeadingNumber";
import { IsEqual } from "./IsEqual";

/**
 * Parses a human-readable file size string and returns its numeric value in bytes.
 *
 * Supports both SI (e.g., kB, MB, GB) and IEC (e.g., KiB, MiB, GiB) units, as well as "Bytes".
 * If no unit is specified, the numeric value is returned as-is.
 * Returns `undefined` if the input is invalid or the unit is unrecognized.
 *
 * @param fileSize - The file size string to parse (e.g., "10 MB", "5.5 GiB", "1000").
 * @returns The file size in bytes as a number, or `undefined` if parsing fails.
 * 
 * @since 3.0.0
 * @version 2023-11-25
 */
export function ParseFileSize(fileSize: string): number | undefined {
    fileSize = fileSize.trim();
    const n = GetLeadingNumber(fileSize, true);
    if (n.length === 0) {
        return undefined;
    }

    const size = parseFloat(n);

    const unit = fileSize.substring(n.length).trim();
    if (unit.length === 0) {
        return size;
    }

    const unitMap: { [key: string]: number } = {
        Bytes: 1,

        kB: Math.pow(1e3, 1),
        MB: Math.pow(1e3, 2),
        GB: Math.pow(1e3, 3),
        TB: Math.pow(1e3, 4),
        PB: Math.pow(1e3, 5),
        EB: Math.pow(1e3, 6),
        ZB: Math.pow(1e3, 7),
        YB: Math.pow(1e3, 8),

        kiB: Math.pow(1024, 1),
        MiB: Math.pow(1024, 2),
        GiB: Math.pow(1024, 3),
        TiB: Math.pow(1024, 4),
        PiB: Math.pow(1024, 5),
        EiB: Math.pow(1024, 6),
        ZiB: Math.pow(1024, 7),
        YiB: Math.pow(1024, 8),
    };

    for (const key of Object.keys(unitMap)) {
        if (IsEqual(unit, key, true)) {
            return size * unitMap[key];
        }
    }

    return undefined;
}
